import {
  ArrowDown,
  ArrowSquareOut,
  Check,
  Copy,
  DiceFive,
  ImageBroken,
  MagnifyingGlass,
  Moon,
  Printer,
  ShareNetwork,
  Sun,
  X,
} from "@phosphor-icons/react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import catalogData from "./data/catalog.json";

gsap.registerPlugin(ScrollTrigger);

type Product = {
  id: string;
  name: string;
  family: string;
  familyLabel: string;
  region: string;
  edition: string;
  sourceName: string;
  sourceUrl: string;
  imageSource: string;
  image: string;
  alcoholic: boolean;
  current: boolean;
  ordinal: number;
};

type ScopeFilter = "all" | "current" | "archive" | "japan" | "alcoholic";
type SortMode = "catalog" | "az" | "missing";
type Theme = "dark" | "light";

const catalog = catalogData as Product[];
const orderedIds = catalog.map((product) => product.id);
const familyOrder = [
  "original",
  "collaboration",
  "ultra",
  "juice",
  "punch-dub",
  "java",
  "rehab",
  "reserve",
  "dragon-tea",
  "nitro",
  "muscle",
  "hydro",
  "mutant",
  "beast",
];
const featuredIds = [
  "us-original-green",
  "us-zero-ultra",
  "us-viking-berry",
  "us-mean-bean",
  "us-tea-lemonade",
];

function findProduct(id: string) {
  return catalog.find((product) => product.id === id) ?? catalog[0];
}

function encodeCollection(values: Set<string>) {
  const bytes = new Uint8Array(Math.ceil(orderedIds.length / 8));
  orderedIds.forEach((id, index) => {
    if (values.has(id)) bytes[Math.floor(index / 8)] |= 1 << (index % 8);
  });
  return btoa(String.fromCharCode(...bytes))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll("=", "");
}

function decodeCollection(encoded: string) {
  const normalized = encoded.replaceAll("-", "+").replaceAll("_", "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const bytes = Uint8Array.from(atob(padded), (character) =>
    character.charCodeAt(0),
  );
  return new Set(
    orderedIds.filter((_, index) => {
      const byte = bytes[Math.floor(index / 8)] ?? 0;
      return (byte & (1 << (index % 8))) !== 0;
    }),
  );
}

function readInitialCollection() {
  const shared = new URLSearchParams(window.location.search).get("set");
  if (shared) {
    try {
      return decodeCollection(shared);
    } catch {
      return new Set<string>();
    }
  }
  try {
    const saved = JSON.parse(
      window.localStorage.getItem("beast-index-collection-v2") ?? "[]",
    ) as string[];
    return new Set(saved.filter((id) => orderedIds.includes(id)));
  } catch {
    return new Set<string>();
  }
}

function readInitialTheme(): Theme {
  const saved = window.localStorage.getItem("beast-index-theme");
  if (saved === "dark" || saved === "light") return saved;
  return "dark";
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function ArchiveImage({
  product,
  eager = false,
  decorative = false,
}: {
  product: Product;
  eager?: boolean;
  decorative?: boolean;
}) {
  const [state, setState] = useState<"loading" | "loaded" | "error">("loading");

  return (
    <span
      className={`archive-image is-${state} ${
        product.current ? "is-packshot" : "is-photo"
      }`}
    >
      {state === "loading" && <span className="image-skeleton" aria-hidden="true" />}
      {state === "error" && (
        <span className="image-fallback">
          <ImageBroken size={24} />
          Image unavailable
        </span>
      )}
      <img
        src={product.image}
        alt={decorative ? "" : product.name}
        width="640"
        height="430"
        loading={eager ? "eager" : "lazy"}
        fetchPriority={eager ? "high" : "auto"}
        onLoad={() => setState("loaded")}
        onError={() => setState("error")}
      />
    </span>
  );
}

function App() {
  const pageRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [collected, setCollected] = useState<Set<string>>(readInitialCollection);
  const [scope, setScope] = useState<ScopeFilter>("all");
  const [family, setFamily] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortMode>("catalog");
  const [theme, setTheme] = useState<Theme>(readInitialTheme);
  const [toast, setToast] = useState("");
  const [spotlightId, setSpotlightId] = useState<string | null>(null);

  const totalCount = catalog.length;
  const currentCount = catalog.filter((product) => product.current).length;
  const archiveCount = totalCount - currentCount;
  const completion = Math.round((collected.size / totalCount) * 100);

  const familyStats = useMemo(
    () =>
      familyOrder
        .map((familyId) => {
          const products = catalog.filter(
            (product) => product.family === familyId,
          );
          return {
            id: familyId,
            label: products[0]?.familyLabel ?? familyId,
            total: products.length,
            collected: products.filter((product) => collected.has(product.id))
              .length,
          };
        })
        .filter((item) => item.total > 0),
    [collected],
  );

  const filteredProducts = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    const visible = catalog.filter((product) => {
      const matchesScope =
        scope === "all" ||
        (scope === "current" && product.current) ||
        (scope === "archive" && !product.current) ||
        (scope === "japan" && product.region === "Japan") ||
        (scope === "alcoholic" && product.alcoholic);
      const matchesFamily = family === "all" || product.family === family;
      const matchesSearch =
        needle.length === 0 ||
        [
          product.name,
          product.familyLabel,
          product.region,
          product.edition,
        ].some((value) => value.toLocaleLowerCase().includes(needle));
      return matchesScope && matchesFamily && matchesSearch;
    });

    if (sort === "az") {
      return [...visible].sort((a, b) => a.name.localeCompare(b.name));
    }
    if (sort === "missing") {
      return [...visible].sort(
        (a, b) => Number(collected.has(a.id)) - Number(collected.has(b.id)),
      );
    }
    return visible;
  }, [collected, family, query, scope, sort]);

  const shareUrl = useMemo(() => {
    const url = new URL(window.location.href);
    url.search = "";
    url.hash = "";
    url.searchParams.set("set", encodeCollection(collected));
    return url.toString();
  }, [collected]);

  useEffect(() => {
    window.localStorage.setItem(
      "beast-index-collection-v2",
      JSON.stringify([...collected]),
    );
  }, [collected]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    window.localStorage.setItem("beast-index-theme", theme);
  }, [theme]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useGSAP(
    () => {
      if (prefersReducedMotion()) return;

      const intro = gsap.timeline({ defaults: { ease: "power3.out" } });
      intro
        .from(".hero-copy > *", {
          opacity: 0,
          y: 28,
          duration: 0.7,
          stagger: 0.07,
        })
        .from(
          ".hero-can",
          {
            opacity: 0,
            y: 48,
            scale: 0.94,
            duration: 0.72,
            stagger: 0.08,
          },
          "-=0.48",
        )
        .from(
          ".progress-dial",
          { opacity: 0, scale: 0.82, duration: 0.5 },
          "-=0.36",
        );

      gsap.utils.toArray<HTMLElement>(".reveal-block").forEach((block) => {
        gsap.from(block, {
          opacity: 0,
          y: 34,
          duration: 0.75,
          ease: "power3.out",
          scrollTrigger: {
            trigger: block,
            start: "top 86%",
            once: true,
          },
        });
      });
    },
    { scope: pageRef },
  );

  function toggleProduct(id: string) {
    setCollected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function clearFilters() {
    setScope("all");
    setFamily("all");
    setQuery("");
    setSort("catalog");
  }

  function openFamily(familyId: string) {
    setScope("all");
    setFamily(familyId);
    setQuery("");
    document.getElementById("collection")?.scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "start",
    });
  }

  function pickNextCan() {
    const missing = catalog.filter((product) => !collected.has(product.id));
    if (missing.length === 0) {
      setToast("The full archive is checked");
      return;
    }
    const picked = missing[Math.floor(Math.random() * missing.length)];
    clearFilters();
    setSpotlightId(picked.id);
    window.setTimeout(() => {
      document.getElementById(`can-${picked.id}`)?.scrollIntoView({
        behavior: prefersReducedMotion() ? "auto" : "smooth",
        block: "center",
      });
    }, 30);
    window.setTimeout(() => setSpotlightId(null), 3200);
  }

  async function copyShareLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setToast("Collection link copied");
    } catch {
      const input = document.createElement("textarea");
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      input.remove();
      setToast("Collection link copied");
    }
  }

  async function shareCollection() {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "My Beast Index collection",
          text: `${collected.size} of ${totalCount} Monster can variants collected`,
          url: shareUrl,
        });
        return;
      } catch {
        return;
      }
    }
    await copyShareLink();
  }

  return (
    <div className="page" ref={pageRef}>
      <header className="site-header">
        <a className="brand" href="#top" aria-label="Beast Index home">
          <span className="brand-mark">B</span>
          <span className="brand-word">
            BEAST <b>INDEX</b>
          </span>
        </a>
        <nav aria-label="Primary navigation">
          <a href="#collection">Collection</a>
          <a href="#export">Share + print</a>
          <a href="#owners">Owners</a>
        </nav>
        <div className="header-actions">
          <a className="header-progress" href="#collection">
            <strong>{collected.size}</strong>
            <span>/ {totalCount}</span>
          </a>
          <button
            className="icon-button"
            type="button"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
          >
            {theme === "dark" ? <Sun size={19} /> : <Moon size={19} />}
          </button>
        </div>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow">THE COMPLETE COLLECTOR ARCHIVE</p>
            <h1>COLLECT THE WHOLE BEAST.</h1>
            <p className="hero-lede">
              Track {currentCount} current US editions and {archiveCount} global,
              regional, and retired can variants.
            </p>
            <div className="hero-buttons">
              <a className="button button-primary" href="#collection">
                Build your set
                <ArrowDown size={19} weight="bold" />
              </a>
              <button
                className="button button-secondary"
                type="button"
                onClick={pickNextCan}
              >
                <DiceFive size={20} />
                Pick my next can
              </button>
            </div>
          </div>

          <div className="hero-stage" aria-label="Featured Monster cans">
            <div className="hero-can-lineup">
              {featuredIds.map((id, index) => (
                <figure className={`hero-can hero-can-${index + 1}`} key={id}>
                  <ArchiveImage
                    product={findProduct(id)}
                    eager={index < 3}
                    decorative
                  />
                </figure>
              ))}
            </div>
            <div
              className="progress-dial"
              style={
                { "--completion": `${completion * 3.6}deg` } as CSSProperties
              }
              aria-label={`${completion}% complete`}
            >
              <div>
                <strong>
                  {collected.size} <span>/ {totalCount}</span>
                </strong>
                <small>collected</small>
              </div>
            </div>
          </div>
        </section>

        <section className="family-ribbon" aria-label="Collection by family">
          {familyStats.map((stat) => (
            <button
              type="button"
              key={stat.id}
              className={family === stat.id ? "is-active" : ""}
              onClick={() => openFamily(stat.id)}
            >
              <span>{stat.label}</span>
              <strong>
                {stat.collected}
                <small>/{stat.total}</small>
              </strong>
            </button>
          ))}
        </section>

        <section className="collection-section" id="collection">
          <div className="section-heading reveal-block">
            <p className="eyebrow">213 exact entries</p>
            <h2>THE COMPLETE CAN LIBRARY.</h2>
            <p>
              Current official packshots meet documented archive photography.
              Every image stays attached to the page that identifies the can.
            </p>
          </div>

          <div className="catalog-tools reveal-block">
            <label className="search-control">
              <MagnifyingGlass size={20} aria-hidden="true" />
              <span className="sr-only">Search flavors</span>
              <input
                ref={searchRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search 213 variants"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                >
                  <X size={18} />
                </button>
              )}
            </label>

            <div className="scope-filter" aria-label="Filter by edition">
              {(
                [
                  ["all", "All"],
                  ["current", "Current US"],
                  ["archive", "Archive"],
                  ["japan", "Japan"],
                  ["alcoholic", "21+"],
                ] as [ScopeFilter, string][]
              ).map(([value, label]) => (
                <button
                  type="button"
                  key={value}
                  className={scope === value ? "is-active" : ""}
                  onClick={() => setScope(value)}
                >
                  {label}
                </button>
              ))}
            </div>

            <label className="select-control">
              <span>Family</span>
              <select
                value={family}
                onChange={(event) => setFamily(event.target.value)}
              >
                <option value="all">All families</option>
                {familyStats.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.label} ({item.total})
                  </option>
                ))}
              </select>
            </label>

            <label className="select-control">
              <span>Sort</span>
              <select
                value={sort}
                onChange={(event) => setSort(event.target.value as SortMode)}
              >
                <option value="catalog">Catalog order</option>
                <option value="az">Name A-Z</option>
                <option value="missing">Missing first</option>
              </select>
            </label>
          </div>

          <div className="catalog-result-bar">
            <span>{filteredProducts.length} variants shown</span>
            <strong>{completion}% complete</strong>
          </div>

          {filteredProducts.length > 0 ? (
            <div className="product-grid">
              {filteredProducts.map((product) => {
                const isCollected = collected.has(product.id);
                return (
                  <article
                    id={`can-${product.id}`}
                    className={`product-card ${
                      isCollected ? "is-collected" : ""
                    } ${product.current ? "is-current" : "is-archive"} ${
                      spotlightId === product.id ? "is-spotlighted" : ""
                    }`}
                    key={product.id}
                  >
                    <button
                      className="product-toggle"
                      type="button"
                      onClick={() => toggleProduct(product.id)}
                      aria-pressed={isCollected}
                      aria-label={`${isCollected ? "Remove" : "Add"} ${
                        product.name
                      } ${isCollected ? "from" : "to"} collection`}
                    >
                      <span className="can-frame">
                        <span className="product-number">
                          {String(product.ordinal).padStart(3, "0")}
                        </span>
                        <ArchiveImage product={product} decorative />
                        <span className="edition-tag">
                          {product.current ? "Current US" : product.edition}
                        </span>
                      </span>
                      <span className="product-copy">
                        <span>
                          <strong>{product.name}</strong>
                          <small>
                            {product.familyLabel} / {product.region}
                          </small>
                        </span>
                        <span className="check-box" aria-hidden="true">
                          {isCollected && <Check size={18} weight="bold" />}
                        </span>
                      </span>
                    </button>
                    <a
                      className="source-link"
                      href={product.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {product.sourceName}
                      <ArrowSquareOut size={14} />
                    </a>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <MagnifyingGlass size={34} />
              <h3>No cans match that search.</h3>
              <p>Try another flavor name or reset the archive filters.</p>
              <button
                className="button button-secondary"
                type="button"
                onClick={clearFilters}
              >
                Show all cans
              </button>
            </div>
          )}
        </section>

        <section className="export-section" id="export">
          <div className="export-score reveal-block">
            <p className="eyebrow">Your collection</p>
            <h2>MAKE YOUR SET OFFICIAL.</h2>
            <div className="giant-score">
              <strong>{collected.size}</strong>
              <span>/ {totalCount}</span>
            </div>
            <p className="score-caption">CANS COLLECTED</p>
            <div className="family-score-grid">
              {familyStats.map((stat) => (
                <div key={stat.id}>
                  <span>{stat.label}</span>
                  <strong>
                    {stat.collected}
                    <small>/{stat.total}</small>
                  </strong>
                </div>
              ))}
            </div>
          </div>

          <div className="share-panel reveal-block">
            <ShareNetwork size={32} />
            <h3>SHARE YOUR COLLECTION</h3>
            <p>
              Copy a compact link that recreates every checked can, or open the
              clean checklist and save it as a PDF.
            </p>
            <label className="share-field">
              <span className="sr-only">Shareable collection link</span>
              <input
                value={shareUrl}
                readOnly
                onFocus={(event) => event.target.select()}
              />
            </label>
            <div className="share-actions">
              <button
                className="button button-primary"
                type="button"
                onClick={copyShareLink}
              >
                <Copy size={20} />
                Copy link
              </button>
              <button
                className="button button-secondary"
                type="button"
                onClick={shareCollection}
              >
                <ShareNetwork size={20} />
                Share
              </button>
              <button
                className="button button-secondary"
                type="button"
                onClick={() => window.print()}
              >
                <Printer size={20} />
                Print PDF
              </button>
            </div>
            <div className="print-preview" aria-hidden="true">
              <div className="preview-head">
                <span>BEAST INDEX</span>
                <strong>
                  {collected.size}/{totalCount}
                </strong>
              </div>
              <div className="preview-grid">
                {catalog.slice(0, 24).map((product) => (
                  <span
                    key={product.id}
                    className={collected.has(product.id) ? "is-checked" : ""}
                  >
                    <img src={product.image} alt="" />
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer id="owners">
        <div className="brand footer-brand">
          <span className="brand-mark">B</span>
          <span className="brand-word">
            BEAST <b>INDEX</b>
          </span>
        </div>
        <div className="owner-lockup">
          <span>SITE OWNERS</span>
          <strong>@lhjj_56</strong>
          <strong>@rawasy95._</strong>
        </div>
        <p>
          Independent fan-made collector index. Monster Energy names, marks,
          and product art belong to their respective owners. Caffeinated and
          alcoholic products are for appropriate-age consumers.
        </p>
        <div className="footer-links">
          <a
            href="https://www.monsterenergy.com/en-us/energy-drinks/"
            target="_blank"
            rel="noreferrer"
          >
            Official catalog
            <ArrowSquareOut size={14} />
          </a>
          <a
            href="https://www.energydrinkmania.net/en/monster_energy/"
            target="_blank"
            rel="noreferrer"
          >
            Photo archive
            <ArrowSquareOut size={14} />
          </a>
        </div>
      </footer>

      {toast && (
        <div className="toast" role="status">
          <Check size={18} weight="bold" />
          {toast}
        </div>
      )}

      <PrintSheet collected={collected} familyStats={familyStats} />
    </div>
  );
}

function PrintSheet({
  collected,
  familyStats,
}: {
  collected: Set<string>;
  familyStats: {
    id: string;
    label: string;
    total: number;
    collected: number;
  }[];
}) {
  return (
    <section className="print-sheet" aria-hidden="true">
      <header>
        <div>
          <strong>BEAST INDEX</strong>
          <span>GLOBAL MONSTER COLLECTION CHECKLIST</span>
        </div>
        <p>
          <b>{collected.size}</b> / {catalog.length}
          <span>CANS COLLECTED</span>
        </p>
      </header>
      {familyStats.map((group) => (
        <section className="print-family" key={group.id}>
          <div className="print-family-heading">
            <h2>{group.label}</h2>
            <span>
              {group.collected}/{group.total}
            </span>
          </div>
          <div className="print-product-grid">
            {catalog
              .filter((product) => product.family === group.id)
              .map((product) => (
                <article
                  className={collected.has(product.id) ? "is-collected" : ""}
                  key={product.id}
                >
                  <span className="print-check">
                    {collected.has(product.id) ? "X" : ""}
                  </span>
                  <img src={product.image} alt="" />
                  <p>{product.name}</p>
                  <small>{product.edition}</small>
                </article>
              ))}
          </div>
        </section>
      ))}
      <footer>
        <span>beast-index.pages.dev</span>
        <span>Owned by @lhjj_56 + @rawasy95._</span>
        <span>{new Date().toLocaleDateString()}</span>
      </footer>
    </section>
  );
}

export default App;
